import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardActionArea, CardContent,
  Chip, Stack, Divider, Alert, Button,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Extension as ExtensionIcon,
  Android as AndroidIcon,
  PhoneIphone as IosIcon,
  ArrowForward as ArrowIcon,
  Dashboard as DashIcon,
  Launch as LaunchIcon,
  Engineering as StaffIcon,
} from '@mui/icons-material';
import { STUDENT, TEACHER, CLASS, CLIENT, IMPACT } from './sharedMockData';

/**
 * Unified mock gallery — single entry point showing every platform's mocks
 * side by side. Four sections, each with a rationale callout and a card grid.
 *
 * Screenshots are the PNGs captured during each swarm and served via the
 * Nest backend's `/mockup-assets` static dir.
 */

// Visual treatment for each mock card. The screenshots are stored in
// /root/automatrix/test-artifacts/... and are static — we serve them via
// a simple fallback to the extension-download public path. If they aren't
// reachable we fall back to an icon-only treatment so the grid still renders.
const ART = {
  DASHBOARD_PREFIX: '/mockup-assets/dashboard',
  EXTV5_PREFIX:     '/mockup-assets/ext-v5',
  ANDROID_PREFIX:   '/mockup-assets/android',
  IOS_PREFIX:       '/mockup-assets/ios',
};

const DASHBOARD_MOCKS = [
  { key: 'scripts',  title: 'Scripts List',       summary: 'Per-app grouped table with drill-down execution history.',                    link: '/mockups',         icon: <DashIcon /> },
  { key: 'watch',    title: 'Watch Mode (CDP)',   summary: 'Split-pane live CDP screencast while a headless Playwright run is running.', link: '/mockups',         icon: <DashIcon /> },
  { key: 'ext',      title: 'Extension Mode',     summary: 'In-browser sidebar review tied to the same credential store as the backend.', link: '/mockups',        icon: <ExtensionIcon /> },
  { key: 'content',  title: 'Content Filtering',  summary: 'Runtime-fetched DNR rules + per-org allowlists / blocklists.',                link: '/mockups',         icon: <DashIcon /> },
];

const EXTV5_MOCKS = [
  { key: 'toolbar',        title: 'Toolbar icon',        summary: 'Pinned Chrome icon — normal vs. disconnected (red-dot overlay). The entire visible surface most of the time.', img: `${ART.EXTV5_PREFIX}/01-toolbar.png`,       icon: <ExtensionIcon /> },
  { key: 'popup',          title: 'Popup · happy path',  summary: '300×200 status popup. Logo, version, Connected dot, optional last-sign-in, "View details" link. No script list.', img: `${ART.EXTV5_PREFIX}/02-popup.png`,          icon: <ExtensionIcon /> },
  { key: 'errors',         title: 'Popup · degraded',    summary: 'Offline, out-of-date, unauthenticated. Same 300×200 popup with one corrective action each.',                   img: `${ART.EXTV5_PREFIX}/03-errors.png`,         icon: <ExtensionIcon /> },
  { key: 'background-run', title: 'Background run',      summary: `When a script runs, the user sees a ~4s toast: "Auto-signed into Khan Academy". That is the entire UX.`,       img: `${ART.EXTV5_PREFIX}/04-background-run.png`, icon: <ExtensionIcon /> },
];

const EXTADMIN_MOCKS = [
  { key: 'sidebar',    title: 'Authoring sidebar',      summary: 'Script picker, step list, transport bar, runtime-fetched indicator. Staff/dev only.',                    img: `${ART.EXTV5_PREFIX}/admin-02-sidebar.png`,   icon: <StaffIcon /> },
  { key: 'recording',  title: 'Recording state',        summary: 'Red-dot timer, live step capture, "Analyzing…" overlay. Staff-facing.',                                 img: `${ART.EXTV5_PREFIX}/admin-03-recording.png`, icon: <StaffIcon /> },
  { key: 'agent',      title: 'Agent Mode',             summary: `Reasoning stream + approval queue. Agent rostering ${STUDENT.full} in ${CLASS.section}. Engineers only.`, img: `${ART.EXTV5_PREFIX}/admin-04-agent-mode.png`, icon: <StaffIcon /> },
  { key: 'first-run',  title: 'First run (rich)',       summary: 'Connecting → fetched → Ready. Full-surface variant. End-user variant is a tiny "Connected" pill.',       img: `${ART.EXTV5_PREFIX}/admin-05-first-run.png`, icon: <StaffIcon /> },
];

const ANDROID_MOCKS = [
  { key: 'login',          title: 'Login',          summary: `${STUDENT.full} signing into AchieveDXP in the parent WebView.`,          img: `${ART.ANDROID_PREFIX}/login.png`,          icon: <AndroidIcon /> },
  { key: 'dashboard',      title: 'Dashboard',      summary: `Tile grid with real partner logos — Khan, iCivics, ReadTheory, etc.`,    img: `${ART.ANDROID_PREFIX}/dashboard.png`,      icon: <AndroidIcon /> },
  { key: 'partner-launch', title: 'Partner Launch', summary: `Three stages of Layer 3 injection. Landed tab shows ${STUDENT.khanUser}.`, img: `${ART.ANDROID_PREFIX}/partner-launch.png`, icon: <AndroidIcon /> },
  { key: 'progress',       title: 'Progress',       summary: `${IMPACT.total.toLocaleString()} impact points · ${IMPACT.weekDelta} this week · shared timeline.`, img: `${ART.ANDROID_PREFIX}/progress.png`, icon: <AndroidIcon /> },
  { key: 'join-class',     title: 'Join Class',     summary: `6-char code entry — lands in ${CLASS.displayName} with ${TEACHER.short}.`, img: `${ART.ANDROID_PREFIX}/join-class.png`,    icon: <AndroidIcon /> },
  { key: 'profile',        title: 'Profile',        summary: `${STUDENT.full} · ${CLIENT.longName}. Classes + stats + settings.`,       img: `${ART.ANDROID_PREFIX}/profile.png`,        icon: <AndroidIcon /> },
];

const IOS_MOCKS = [
  { key: 'login',          title: 'Login',          summary: `${STUDENT.full} signing into AchieveDXP in WKWebView.`,                   img: `${ART.IOS_PREFIX}/login.png`,          icon: <IosIcon /> },
  { key: 'dashboard',      title: 'Dashboard',      summary: `Tile grid using the same partner logos as Android (unified CDN).`,       img: `${ART.IOS_PREFIX}/dashboard.png`,      icon: <IosIcon /> },
  { key: 'partner-launch', title: 'Partner Launch', summary: `L3 injection then landed child WKWebView — ${STUDENT.khanUser}.`,        img: `${ART.IOS_PREFIX}/partner-launch.png`, icon: <IosIcon /> },
  { key: 'progress',       title: 'Progress',       summary: `Same ${IMPACT.total.toLocaleString()} impact points as Android.`,         img: `${ART.IOS_PREFIX}/progress.png`,       icon: <IosIcon /> },
  { key: 'join-class',     title: 'Join Class',     summary: `iOS OTP-style input + hardware keyboard.`,                                img: `${ART.IOS_PREFIX}/join-class.png`,     icon: <IosIcon /> },
  { key: 'profile',        title: 'Profile',        summary: `${STUDENT.full} · ${CLIENT.longName}.`,                                   img: `${ART.IOS_PREFIX}/profile.png`,        icon: <IosIcon /> },
];

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------

const PlatformHeader = ({ icon, title, chipLabel, chipColor, tagline }) => (
  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
    <Box sx={{
      width: 36, height: 36, borderRadius: 2,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: `${chipColor}15`, color: chipColor,
    }}>
      {icon}
    </Box>
    <Box>
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {tagline}
      </Typography>
    </Box>
    <Box sx={{ flexGrow: 1 }} />
    {chipLabel && <Chip size="small" label={chipLabel} sx={{ fontWeight: 700, bgcolor: `${chipColor}20`, color: chipColor }} />}
  </Stack>
);

const MockCard = ({ m, accent }) => {
  // Swap to icon-only fallback when the image 404s. Keeps the gallery from
  // showing broken-image glyphs for aspirational mocks that don't have a PNG yet.
  const [imgBroken, setImgBroken] = React.useState(false);
  const showImg = Boolean(m.img) && !imgBroken;
  return (
  <Card
    elevation={0}
    sx={{
      border: '1px solid #e0e0e0', borderRadius: 2, height: '100%',
      overflow: 'hidden',
      transition: 'transform 0.15s, box-shadow 0.15s',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(16,24,40,0.08)' },
    }}
  >
    <CardActionArea
      component={m.link ? RouterLink : 'div'}
      to={m.link}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
    >
      {/* Image header */}
      {showImg ? (
        <Box sx={{
          height: 160,
          bgcolor: '#f6f7fb',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
          borderBottom: `2px solid ${accent}`,
        }}>
          <Box
            component="img"
            src={m.img}
            alt={m.title}
            sx={{
              maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
            }}
            onError={() => setImgBroken(true)}
          />
        </Box>
      ) : (
        <Box sx={{
          height: 160, bgcolor: `${accent}12`, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          color: accent, fontSize: 44,
          borderBottom: `2px solid ${accent}`,
        }}>
          {m.icon || <DashIcon sx={{ fontSize: 44 }} />}
        </Box>
      )}
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5, fontSize: '0.95rem' }}>
          {m.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem', lineHeight: 1.45 }}>
          {m.summary}
        </Typography>
      </CardContent>
    </CardActionArea>
  </Card>
  );
};

const PlatformSection = ({
  icon, title, chipLabel, chipColor, tagline,
  rationale, linkTo, linkLabel, mocks, accent,
}) => (
  <Box sx={{ mb: 5 }}>
    <PlatformHeader icon={icon} title={title} chipLabel={chipLabel} chipColor={chipColor} tagline={tagline} />
    <Alert
      severity="info"
      sx={{
        mb: 2,
        bgcolor: `${accent}08`,
        borderLeft: `4px solid ${accent}`,
        '& .MuiAlert-icon': { color: accent },
      }}
      action={linkTo && (
        <Button
          component={RouterLink}
          to={linkTo}
          size="small"
          endIcon={<ArrowIcon />}
          sx={{ textTransform: 'none', color: accent, fontWeight: 700 }}
        >
          {linkLabel || 'Open detail'}
        </Button>
      )}
    >
      {rationale}
    </Alert>
    <Grid container spacing={2}>
      {mocks.map(m => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={m.key}>
          <MockCard m={m} accent={accent} />
        </Grid>
      ))}
    </Grid>
  </Box>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const AllMocksIndex = () => (
  <Box sx={{ maxWidth: 1400, mx: 'auto', pb: 6 }}>
    {/* Hero */}
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <PaletteIcon sx={{ fontSize: 32, color: '#1976d2' }} />
        <Typography variant="h4" fontWeight={700}>
          Automatrix Mock Gallery
        </Typography>
        <Chip label="MOCKUP" size="small" color="warning" sx={{ fontWeight: 700 }} />
        <Chip label="Unified narrative" size="small" sx={{ fontWeight: 600, bgcolor: '#e3f2fd', color: '#0d47a1' }} />
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 960 }}>
        One place to review every Automatrix UI surface. All mocks share the same narrative:
        teacher <b>{TEACHER.full}</b> rosters student <b>{STUDENT.full}</b> into{' '}
        <b>{CLASS.displayName}</b> at <b>{CLIENT.longName}</b>. Logos are sourced from the
        real Nucleos integration CDN so the side-by-side comparison is accurate.
      </Typography>
      <Alert severity="info" variant="outlined" sx={{ mt: 2, maxWidth: 960 }}>
        <b>Brand split:</b> User-facing surfaces (Extension v5 end-user, Android, iOS)
        show the <b>AchieveDXP</b> brand — that's what inmates on facility Chromebooks
        and tablets see. Internal tooling (Automatrix dashboard, ext-admin) keeps the{' '}
        <b>Automatrix</b> brand; that's what the Nucleos team uses to manage AchieveDXP.
      </Alert>
    </Box>

    {/* Persona card — visual narrative anchor */}
    <Card elevation={0} sx={{
      mb: 4,
      border: '1px solid #e0e0e0',
      borderRadius: 3,
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e3f2fd 100%)',
    }}>
      <CardContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center">
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="overline" color="primary" fontWeight={700}>
              Narrative anchor
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
              {STUDENT.full} · {STUDENT.grade} · {CLIENT.longName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Teacher {TEACHER.full} runs {CLASS.displayName}. Class code <Box
                component="code"
                sx={{ bgcolor: '#fff', px: 0.75, py: 0.25, borderRadius: 0.5, fontWeight: 700 }}
              >{CLASS.code}</Box> is used in every Join Class mock.
              {' '}Impact points: <b>{IMPACT.total.toLocaleString()}</b> (+{IMPACT.weekDelta} this week),
              {' '}{IMPACT.streakDays}-day streak, {IMPACT.badges} badges, {IMPACT.classesJoined} classes.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip label={`Student: ${STUDENT.short}`}  sx={{ fontWeight: 700, bgcolor: '#fff' }} />
            <Chip label={`Teacher: ${TEACHER.short}`}  sx={{ fontWeight: 700, bgcolor: '#fff' }} />
            <Chip label={`Client: ${CLIENT.name}`}     sx={{ fontWeight: 700, bgcolor: '#fff' }} />
          </Stack>
        </Stack>
      </CardContent>
    </Card>

    {/* Automatrix Dashboard */}
    <PlatformSection
      icon={<DashIcon />}
      title="Automatrix Dashboard"
      tagline="Web control plane used by operators"
      chipLabel="Web · MUI"
      chipColor="#1976d2"
      accent="#1976d2"
      rationale="The operator-facing dashboard at automatrix.nucleos.com. Used by Nucleos staff to author scripts, watch live executions via CDP, review credential-vault health, and curate content filters. All four mocks render inside the same Layout frame as the real product."
      linkTo="/mockups"
      linkLabel="Open dashboard tabs"
      mocks={DASHBOARD_MOCKS}
    />

    <Divider sx={{ mb: 4 }} />

    {/* Extension v5 — end-user runtime */}
    <PlatformSection
      icon={<ExtensionIcon />}
      title="Extension v5 — end-user runtime"
      tagline="Silent script runner for facility Chromebooks"
      chipLabel="Chrome MV3 · end-user"
      chipColor="#7b1fa2"
      accent="#7b1fa2"
      rationale="Replaces the current AchieveDXP extension on facility Chromebooks. End users (inmates) see approximately nothing — a toolbar icon, a small 300x200 popup showing connected status + last sign-in, and an occasional 4-second toast when a script autosigns them into a partner service. No sidebar, no recording, no agent UI. All authoring lives in the Automatrix web dashboard."
      linkTo="/mockups/ext-v5"
      linkLabel="Open end-user gallery"
      mocks={EXTV5_MOCKS}
    />

    <Divider sx={{ mb: 4 }} />

    {/* Staff / Dev Extension — authoring in-browser */}
    <PlatformSection
      icon={<StaffIcon />}
      title="Staff / Dev Extension — authoring-in-browser"
      tagline="Hypothetical power-user surface for engineers debugging live sessions"
      chipLabel="Chrome MV3 · staff"
      chipColor="#4a148c"
      accent="#4a148c"
      rationale="Hypothetical staff / developer-facing extension surface for power users who want to author and test scripts in live browser sessions. NOT what end-users see. Most of this functionality already lives in the Automatrix web dashboard (Watch Mode, Agent Mode, script editor); this surface is for engineers who want the authoring tools co-located with real partner tabs, without needing DevTools + the dashboard open side-by-side."
      linkTo="/mockups/ext-admin"
      linkLabel="Open staff/dev gallery"
      mocks={EXTADMIN_MOCKS}
    />

    <Divider sx={{ mb: 4 }} />

    {/* Android */}
    <PlatformSection
      icon={<AndroidIcon />}
      title="AchieveDXP Android Port"
      tagline="Kotlin / Jetpack Compose + native WebView"
      chipLabel="Android · Material 3"
      chipColor="#3ddc84"
      accent="#2e7d32"
      rationale="Native Android port of the AchieveDXP student experience. Parent WebView hosts AchieveDXP; partner apps (Khan, iCivics, ReadTheory, …) launch through one of three auth layers driven by /api/mobile/app-launch/:appKey. All six phone mocks use real partner logos from the Nucleos integration CDN."
      linkTo="/mockups/android"
      linkLabel="Open Android gallery"
      mocks={ANDROID_MOCKS}
    />

    <Divider sx={{ mb: 4 }} />

    {/* iOS */}
    <PlatformSection
      icon={<IosIcon />}
      title="AchieveDXP iOS Port"
      tagline="SwiftUI + WKWebView following Apple HIG"
      chipLabel="iOS 17+ · HIG"
      chipColor="#007AFF"
      accent="#007AFF"
      rationale="Native iOS port wrapping AchieveDXP in WKWebView. Follows Apple HIG (large titles, translucent nav, tab bar, SF font stack, iOS blue #007AFF). Same narrative as Android so the platforms can be compared side-by-side during design review."
      linkTo="/mockups/ios"
      linkLabel="Open iOS gallery"
      mocks={IOS_MOCKS}
    />

    {/* Footer nav */}
    <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid #e0e0e0' }}>
      <Typography variant="body2" color="text.secondary">
        Source design docs are in <code>docs/</code>:{' '}
        <code>EXTENSION_V5_DESIGN.md</code>,{' '}
        <code>ACHIEVEDXP_ANDROID_DESIGN.md</code>,{' '}
        <code>ACHIEVEDXP_IOS_DESIGN.md</code>. Review-process gaps surfaced by this
        mockup exercise live in <code>docs/MOCK_REVEALED_GAPS.md</code>.
      </Typography>
    </Box>
  </Box>
);

export default AllMocksIndex;
